import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import { useViewport } from "../../helpers";
import { myExpariencesService } from "../../services";
import SettingsIcon from "@mui/icons-material/Settings";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import HdrManagement from "./HdrManagement";

const PREFIX = "hdrmanager";

const classes = {
  hdrMainStyle: `${PREFIX}-hdrMainStyle`,
  hdrButtonStyle: `${PREFIX}-hdrButtonStyle`,
  selectStyle: `${PREFIX}-selectStyle`,
  settingsBar: `${PREFIX}-settingsBar`,
  hdrTitle: `${PREFIX}-hdrTitle`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.hdrMainStyle}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      margin: "0 auto",
      maxWidth: "1080px",
      minHeight: "340px",
    },
    [`& .${classes.hdrButtonStyle}`]: {
      marginRight: "1rem",
      width: "10%",
    },
    [`& .${classes.selectStyle}`]: {
      width: "50%",
    },
    [`& .${classes.settingsBar}`]: {
      display: "flex",
      justifyContent: "center",
      width: "70%",
      maxWidth: "650px",
      marginTop: "2rem",
    },
    [`& .${classes.hdrTitle}`]: {
      marginTop: "2rem",
      [theme.breakpoints.down("sm")]: {
        marginTop: "1rem",
      },
      marginBottom: "1rem",
      textAlign: "start",
      width: "100%",
      paddingLeft: "10px",
    },
  };
});

export default function HdrManager() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [allHdr, setAllHdr] = useState(null);
  const [skybox, setSkybox] = useState(null);
  const [modalToggle, setModalToggle] = useState(false);
  const { width } = useViewport();

  useEffect(() => {
    myExpariencesService
      .getAllHdr()
      .then((res) => {
        setAllHdr(res);
        setSkybox(res.length > 0 ? res[0].url : "");
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.getFail"), { variant: "error" });
      });
  }, []);

  if (!allHdr || skybox === null)
    return (
      <Container>
        <div className="loading-container">
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Container>
    );
  else
    return (
      <StyledDiv>
        <ResponsiveDialogManager
          title={t("Hdr.hdrManagement")}
          fullScreen
          open={modalToggle}
          onClose={() => {
            setModalToggle(false);
          }}
          renderer={
            <HdrManagement
              allHdr={allHdr}
              setAllHdr={setAllHdr}
              skybox={skybox}
              setSkybox={setSkybox}
            />
          }
        />
        <div className={classes.hdrMainStyle}>
          <Typography className={classes.hdrTitle} variant="h4">
            {t("Hdr.managementTitle")}
          </Typography>
          <model-viewer
            src={"https://ar-poc.s3.fr-par.scw.cloud/test/design.glb"}
            skybox-image={skybox}
            alt="3D model in VR"
            camera-controls
            id="model-viewer"
            min-field-of-view="70deg"
            max-field-of-view="85deg"
            min-camera-orbit="-Infinity 22.5deg 0.5m"
            max-camera-orbit="Infinity 157.5deg 10m"
            camera-orbit="0deg 90deg 1.1m"
            style={{
              width: "99%",
              height: width > 960 ? "600px" : "400px",
            }}
          >
            <div
              id="lazy-load-poster"
              slot="poster"
              className={classes.webVrPoster}
            ></div>
          </model-viewer>
          <div className={classes.settingsBar}>
            <IconButton
              variant="outlined"
              className={classes.hdrButtonStyle}
              size="large"
              id="add-hdr-button"
              color="primary"
              onClick={(e) => {
                setModalToggle(true);
              }}
            >
              <SettingsIcon />
            </IconButton>
            <Select
              value={skybox}
              className={classes.selectStyle}
              onChange={(e) => {
                setSkybox(e.target.value);
              }}
            >
              {allHdr.map((item, index) => (
                <MenuItem key={`skybox-option-${index + 1}`} value={item.url}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </StyledDiv>
    );
}
